import React, { useState } from 'react'
import { FilePlus } from 'lucide-react'

import { useMember } from '@rewardops/auth'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useTranslation } from '~/app/i18n/client'
import MapleButton from '../button'

const PointsAdjustmentForm = ({
  setIsSubmitted,
  setClaimNumber,
  onClose,
}: {
  setIsSubmitted: (value: boolean) => void
  setClaimNumber: (value: string) => void
  onClose?: () => void
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    purchaseDate: '',
    retailer: '',
    retailerOrderNumber: '',
    pointsAmount: '',
    orderValue: '',
    comments: '',
  })
  const { api, tenant } = useApi()
  const member = useMember()
  const [showDateError, setShowDateError] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    purchaseDate: false,
    retailer: false,
    retailerOrderNumber: false,
    pointsAmount: false,
    orderValue: false,
    file: false,
  })
  const [touchedFields, setTouchedFields] = useState({
    purchaseDate: false,
    retailer: false,
    retailerOrderNumber: false,
    pointsAmount: false,
    orderValue: false,
    file: false,
  })
  const [file, setFile] = useState<{
    base64Data: string
    name: string
    type: string
  }>()
  const [fileErrors, setFileErrors] = useState<string[]>([])

  const createPointsInquiryTicketMutation =
    api.customerSupport.createPointsInquiryTicket.useMutation()

  const handleInputChange = (e: any) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (touchedFields[name as keyof typeof touchedFields]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: value.trim() === '',
      }))
    }

    if (name === 'purchaseDate' && value) {
      const selectedDate = new Date(value)
      const today = new Date()
      const daysDifference = Math.floor(
        (today.getTime() - selectedDate.getTime()) / (1000 * 60 * 60 * 24),
      )
      setShowDateError(daysDifference < 30)
    } else if (name === 'purchaseDate' && !value) {
      setShowDateError(false)
    }
  }

  const handleBlur = (fieldName: any) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }))

    const value = (formData as any)[fieldName] || ''
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: value.trim() === '',
    }))
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]

    setTouchedFields((prev) => ({
      ...prev,
      file: true,
    }))

    if (!selectedFile) {
      setValidationErrors((prev) => ({
        ...prev,
        file: true,
      }))
      return
    }

    // Validate file size of 500kb limit. This limitation is due to how much
    // we can send in a single request since we're sending the file as base64
    if (selectedFile.size > 0.5 * 1024 * 1024) {
      setValidationErrors((prev) => ({
        ...prev,
        file: true,
      }))
      setFileErrors(['File size must be less than 500KB'])
      return
    }

    try {
      // Convert the file to base64 string using FileReader
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Failed to read file as base64'))
          }
        }

        reader.onerror = () => {
          reject(new Error('Error reading file'))
        }

        // This will read the file as a data URL (base64)
        reader.readAsDataURL(selectedFile)
      })

      setFile({
        base64Data,
        name: selectedFile.name,
        type: selectedFile.type,
      })

      setValidationErrors((prev) => ({
        ...prev,
        file: false,
      }))
      setFileErrors([])

      setTouchedFields((prev) => ({
        ...prev,
        file: true,
      }))
    } catch (error) {
      console.error('Error reading file:', error)
      setFileErrors(['Error reading file. Please try again.'])
      setValidationErrors((prev) => ({
        ...prev,
        file: true,
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const response = await createPointsInquiryTicketMutation.mutateAsync({
        firstName: member?.firstName || '',
        lastName: member?.lastName || '',
        email: member?.email || '',
        purchaseDate: formData.purchaseDate,
        retailer: formData.retailer,
        orderNumber: formData.retailerOrderNumber,
        pointsAmount: Number(formData.pointsAmount),
        orderValue: Number(formData.orderValue),
        comments: formData.comments,
        attachment: file
          ? {
              base64Data: file.base64Data,
              fileName: file.name,
              mimeType: file.type,
            }
          : undefined,
      })

      if (response) {
        setClaimNumber(response.id)
        setIsSubmitted(true)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  const validateForm = () => {
    const newValidationErrors = {
      purchaseDate: !formData.purchaseDate,
      retailer: formData.retailer.trim() === '',
      retailerOrderNumber: formData.retailerOrderNumber.trim() === '',
      pointsAmount: formData.pointsAmount.trim() === '',
      orderValue: formData.orderValue.trim() === '',
      file: !file,
    }

    setValidationErrors(newValidationErrors)
    setTouchedFields({
      purchaseDate: true,
      retailer: true,
      retailerOrderNumber: true,
      pointsAmount: true,
      orderValue: true,
      file: true,
    })

    return !Object.values(newValidationErrors).some((error) => error)
  }

  const handleUploadClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    document.getElementById('fileInput')?.click()
  }

  const { daysBeforeClaim } = tenant?.forms?.pointsInquiry || {}

  return (
    <div className="w-full animate-in fade-in duration-300">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-12">
          <div className="text-2xl font-semibold md:text-[28px]">
            {t('pointsAdjustment.title')}
          </div>

          <p className="text-sm text-gray-600">
            {t('pointsAdjustment.notice', { daysBeforeClaim })}
          </p>

          {/* Account Details Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold">
              {t('pointsAdjustment.sections.accountDetails')}
            </h3>
            <div className="space-y-2">
              <div>
                <Label className="text-base">{t('general.name')}:</Label>{' '}
                <span className="font-normal">
                  {member?.firstName} {member?.lastName}
                </span>
              </div>
              <div>
                <Label className="text-base">{t('general.email')}:</Label>{' '}
                <span className="font-normal">{member?.email}</span>
              </div>
            </div>
          </div>

          {/* Order Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold">
              {t('pointsAdjustment.sections.orderDetails')}
            </h3>

            {/* Purchase Date Field */}
            <div className="space-y-2">
              <div>
                <Label className="text-base" htmlFor="purchaseDate">
                  {t('pointsAdjustment.purchaseDate.label')}
                </Label>
                <p className="text-sm text-gray-600">
                  {t('pointsAdjustment.purchaseDate.helper')}
                </p>
              </div>
              <Input
                type="date"
                id="purchaseDate"
                name="purchaseDate"
                className={`w-full ${
                  (touchedFields.purchaseDate && !formData.purchaseDate) ||
                  showDateError
                    ? 'border-red-500'
                    : ''
                }`}
                value={formData.purchaseDate}
                onChange={handleInputChange}
                onBlur={() => handleBlur('purchaseDate')}
              />
              {touchedFields.purchaseDate && !formData.purchaseDate && (
                <ErrorMessage
                  message={t('pointsAdjustment.errors.requiredField')}
                />
              )}
              {showDateError && (
                <ErrorMessage
                  message={t('pointsAdjustment.errors.waitingPeriodError', {
                    daysBeforeClaim,
                  })}
                />
              )}
            </div>

            {/* Retailer Field */}
            <div className="space-y-2">
              <div>
                <Label className="text-base" htmlFor="retailer">
                  {t('pointsAdjustment.retailer.label')}
                </Label>
                <p className="text-sm text-gray-600">
                  {t('pointsAdjustment.retailer.helper')}
                </p>
              </div>
              <Input
                id="retailer"
                name="retailer"
                className={`w-full ${
                  validationErrors.retailer ? 'border-red-500' : ''
                }`}
                value={formData.retailer}
                onChange={handleInputChange}
                onBlur={() => handleBlur('retailer')}
              />
              {touchedFields.retailer && validationErrors.retailer && (
                <ErrorMessage
                  message={t('pointsAdjustment.errors.requiredField')}
                />
              )}
            </div>

            {/* Retailer Order Number Field */}
            <div className="space-y-2">
              <div>
                <Label className="text-base" htmlFor="retailerOrderNumber">
                  {t('pointsAdjustment.orderNumber.label')}
                </Label>
                <p className="text-sm text-gray-600">
                  {t('pointsAdjustment.orderNumber.helper')}
                </p>
              </div>
              <Input
                id="retailerOrderNumber"
                name="retailerOrderNumber"
                className={`w-full ${
                  validationErrors.retailerOrderNumber ? 'border-red-500' : ''
                }`}
                value={formData.retailerOrderNumber}
                onChange={handleInputChange}
                onBlur={() => handleBlur('retailerOrderNumber')}
              />
              {touchedFields.retailerOrderNumber &&
                validationErrors.retailerOrderNumber && (
                  <ErrorMessage
                    message={t('pointsAdjustment.errors.requiredField')}
                  />
                )}
            </div>
          </div>

          {/* Points and Purchase Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold">
              {t('pointsAdjustment.sections.pointsPurchaseDetails')}
            </h3>

            {/* Points Amount Field */}
            <div className="space-y-2">
              <div>
                <Label className="text-base" htmlFor="pointsAmount">
                  {t('pointsAdjustment.pointsAmount.label')}
                </Label>
                <p className="text-sm text-gray-600">
                  {t('pointsAdjustment.pointsAmount.helper')}
                </p>
              </div>
              <Input
                type="number"
                id="pointsAmount"
                name="pointsAmount"
                className={`w-full ${
                  validationErrors.pointsAmount ? 'border-red-500' : ''
                }`}
                value={formData.pointsAmount}
                onChange={handleInputChange}
                onBlur={() => handleBlur('pointsAmount')}
              />
              {touchedFields.pointsAmount && validationErrors.pointsAmount && (
                <ErrorMessage
                  message={t('pointsAdjustment.errors.requiredField')}
                />
              )}
            </div>

            {/* Order Value Field */}
            <div className="space-y-2">
              <div>
                <Label className="text-base" htmlFor="orderValue">
                  {t('pointsAdjustment.orderValue.label')}
                </Label>
                <p className="text-sm text-gray-600">
                  {t('pointsAdjustment.orderValue.helper')}
                </p>
              </div>
              <Input
                id="points-adjustment-order-value"
                type="number"
                name="orderValue"
                className={`w-full ${
                  validationErrors.orderValue ? 'border-red-500' : ''
                }`}
                value={formData.orderValue}
                onChange={handleInputChange}
                onBlur={() => handleBlur('orderValue')}
              />
              {touchedFields.orderValue && validationErrors.orderValue && (
                <ErrorMessage
                  message={t('pointsAdjustment.errors.requiredField')}
                />
              )}
            </div>
          </div>

          {/* Proof of Purchase Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold">
              {t('pointsAdjustment.proofOfPurchase.title')}
            </h3>
            <div>
              <Label className="text-base" htmlFor="proofOfPurchase">
                {t('pointsAdjustment.proofOfPurchase.uploadLabel')}
              </Label>
              <p className="text-sm text-gray-600">
                {t('pointsAdjustment.proofOfPurchase.instructions')}
              </p>
            </div>
            <div className="mt-2">
              <MapleButton
                id="points-adjustment-upload-button"
                type="button"
                variant="outline"
                className={`w-full p-4 text-base font-normal ${
                  validationErrors.file ? 'border-red-500' : ''
                }`}
                onClick={handleUploadClick as any}
                icon={<FilePlus className="h-6 w-6" />}
              >
                {t('pointsAdjustment.proofOfPurchase.buttonText')}
              </MapleButton>
              <input
                id="fileInput"
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                onChange={handleFileChange}
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600">{file.name}</p>
              )}
            </div>
            {touchedFields.file && validationErrors.file && (
              <ErrorMessage
                message={t('pointsAdjustment.errors.requiredField')}
              />
            )}
            {fileErrors.length > 0 && (
              <div className="mt-2 space-y-1">
                {fileErrors.map((error) => (
                  <ErrorMessage key={error} message={error} />
                ))}
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="space-y-2">
            <Textarea
              id="comments"
              name="comments"
              placeholder={t('pointsAdjustment.comments')}
              value={formData.comments}
              onChange={handleInputChange}
              className="min-h-[100px] w-full"
            />
          </div>

          {/* Submit Button */}
          <div className="mt-4">
            <MapleButton
              id="points-adjustment-form-submit"
              className="w-full p-4 text-base font-normal"
              type="submit"
              disabled={showDateError}
              showLoader={createPointsInquiryTicketMutation.isLoading}
            >
              {t('pointsAdjustment.submit')}
            </MapleButton>
          </div>
        </div>
      </form>
    </div>
  )
}

const ErrorMessage = ({ message }: { message: string }) => (
  <div className="text-xs text-red-500">{message}</div>
)

export const PointsAdjustmentFormModal = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { t } = useTranslation()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [claimNumber, setClaimNumber] = useState('')
  const { tenant } = useApi()

  const { responseTimeDays } = tenant?.forms?.pointsAdjustment || {}

  const submittedMessage = (
    <div className="relative flex flex-col gap-12 p-12 animate-in fade-in duration-500">
      <div className="text-[28px] font-semibold">
        {t('pointsAdjustment.title')}
      </div>
      <p className="px-8 text-center">
        {t('pointsAdjustment.successMessage', {
          claimNumber,
          responseTimeDays,
        })}
      </p>
    </div>
  )

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="scrollbar scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 scrollbar-track-rounded hover:scrollbar-track-rounded mx-auto my-[5vh] max-h-[90vh] max-w-3xl overflow-y-auto p-0">
        <DialogTitle className="sr-only">
          {t('pointsAdjustment.title')}
        </DialogTitle>
        {isSubmitted ? (
          submittedMessage
        ) : (
          <PointsAdjustmentForm
            setIsSubmitted={setIsSubmitted}
            setClaimNumber={setClaimNumber}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PointsAdjustmentForm
